// import React, { useEffect, useState } from "react";
// import { useAvatarCache } from "../../../contexts/AvatarContext";
// import DefaultAvatar from "./DefaultAvatar";

// type AvatarSize = "xxxs" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

// interface AvatarImageProps {
//   userId: string;
//   displayName?: string;
//   size?: AvatarSize;
//   className?: string;
// }

// const sizeMap: Record<AvatarSize, string> = {
//   xxxs: "w-6 h-6", // 24px
//   xxs: "w-8 h-8", // 32px
//   xs: "w-10 h-10", // 40px
//   sm: "w-12 h-12", // 48px
//   md: "w-20 h-20",
//   lg: "w-32 h-32",
//   xl: "w-48 h-48",
// };

// const AvatarImage: React.FC<AvatarImageProps> = ({
//   userId,
//   displayName,
//   size = "md",
//   className,
// }) => {
//   const { getAvatarUrl } = useAvatarCache();
//   const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState<string | null>(null);

//   useEffect(() => {
//     let active = true;

//     const load = async () => {
//       setIsLoading(true);
//       try {
//         const url = await getAvatarUrl(userId);
//         if (active) {
//           setAvatarSrc(url || null);
//         }
//       } catch (err) {
//         if (active) {
//           setError("Failed to load avatar");
//           setAvatarSrc(null);
//         }
//       } finally {
//         if (active) setIsLoading(false);
//       }
//     };

//     // Check if image is already cached before loading
//     const checkCache = () => {
//       const img = new Image();
//       const avatarUrl = `/api/user/avatar/${userId}`;
      
//       img.onload = () => {
//         setAvatarSrc(avatarUrl);
//         setIsLoading(false);
//       };
      
//       img.onerror = () => {
//         setIsLoading(false);
//       };
      
//       img.src = avatarUrl;
//     };

//     // Try to load from cache first, then fallback to async load
//     checkCache();
//     load();

//     return () => {
//       active = false;
//     };
//   }, [userId, getAvatarUrl]);

//   const sizeClasses = `rounded-full object-cover ${sizeMap[size]} ${
//     className || ""
//   }`;

//   if (isLoading) {
//     return (
//       <div
//         className={`${sizeClasses} bg-gray-200 animate-pulse flex items-center justify-center`}
//       >
//         <span className="text-xs">...</span>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div
//         className={`${sizeClasses} bg-red-100 border border-red-500 flex items-center justify-center`}
//       >
//         !
//       </div>
//     );
//   }

//   if (avatarSrc) {
//     return (
//       <img
//         src={avatarSrc}
//         alt=""
//         title={displayName}
//         className={sizeClasses}
//         onError={() => setAvatarSrc(null)}
//       />
//     );
//   }

//   return (
//     <DefaultAvatar userId={userId} displayName={displayName} size={size} />
//   );
// };

// export default AvatarImage;
import React, { useEffect, useState } from "react";
import { useAvatarCache } from "../../../contexts/AvatarContext";
import DefaultAvatar from "./DefaultAvatar";

type AvatarSize = "xxxs" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

interface AvatarImageProps {
  userId: string;
  displayName?: string;
  size?: AvatarSize;
  className?: string;
}

const sizeMap: Record<AvatarSize, string> = {
  xxxs: "w-6 h-6", // 24px
  xxs: "w-8 h-8", // 32px
  xs: "w-10 h-10", // 40px
  sm: "w-12 h-12", // 48px
  md: "w-20 h-20",
  lg: "w-32 h-32",
  xl: "w-48 h-48",
};

const AvatarImage: React.FC<AvatarImageProps> = ({
  userId,
  displayName,
  size = "md",
  className,
}) => {
  const { getAvatarUrl, refreshAvatar } = useAvatarCache();
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = await getAvatarUrl(userId);
        if (active) {
          setAvatarSrc(url || null);
        }
      } catch (err) {
        if (active) {
          setError("Failed to load avatar");
          setAvatarSrc(null);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    // Only use the AvatarContext, remove the conflicting checkCache logic
    load();

    return () => {
      active = false;
    };
  }, [userId, getAvatarUrl, retryCount]);

  // Handle manual retry on error
  const handleRetry = () => {
    refreshAvatar(userId);
    setRetryCount(prev => prev + 1);
  };

  const sizeClasses = `rounded-full object-cover ${sizeMap[size]} ${
    className || ""
  }`;

  if (isLoading) {
    return (
      <div
        className={`${sizeClasses} bg-gray-200 animate-pulse flex items-center justify-center`}
      >
        <span className="text-xs">...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`${sizeClasses} bg-red-100 border border-red-500 flex items-center justify-center cursor-pointer`}
        onClick={handleRetry}
        title="Click to retry"
      >
        Shiraz !
      </div>
    );
  }

  if (avatarSrc) {
    return (
      <img
        src={avatarSrc}
        alt=""
        title={displayName}
        className={sizeClasses}
        onError={() => setAvatarSrc(null)}
      />
    );
  }

  return (
    <DefaultAvatar userId={userId} displayName={displayName} size={size} />
  );
};

export default AvatarImage;
